import { useState } from "react";
import useI18n from "@/hooks/useI18n";

interface PlanItem {
  text: string;
  included: boolean;
}

interface PlanRechargeRow {
  recharge: string;
  fee: string;
  balance: string;
}

interface PlanSection {
  id: string;
  title: string;
  items?: PlanItem[];
  description?: string;
  notes?: string[];
  rechargeTable?: PlanRechargeRow[];
  link?: { label: string; href: string };
}

export interface Plan {
  id: string;
  name: string;
  isBestSeller?: boolean;
  badgeLabel?: string;
  promo?: string;
  originalPriceLabel?: string;
  price: string;
  pricePeriod?: string;
  tags?: string[];
  regulationLink?: string;
  ctaLabel: string;
  ctaLink: string;
  summaryHeader?: string;
  summaryFeatures?: string[];
  summaryDisclaimer?: string;
  sections: PlanSection[];
}

const CheckIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 mt-[3px] text-[#00b67a]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.33 8.33l2.84 2.84 6.5-6.5"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 mt-[3px] text-[#d60b52]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FeatureIcon = ({ included }: { included: boolean }) => {
  if (!included) {
    return <CloseIcon />;
  }

  return <CheckIcon />;
};

const FileIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 text-[#0e0d0d]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.5 1.5h5.25L13 5.75v8.75H3.5z"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
    />
    <path
      d="M8.75 1.5v4.25H13"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
    />
    <path
      d="M5.5 9h5M5.5 11.25h5"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function buildPlans(t: (key: string) => string): Plan[] {
  const partnershipUrl =
    "https://www.semparar.com.br/parceria-pedagio-eletronico";
  const praticoRegulationUrl =
    "https://www.semparar.com.br/on/demandware.static/-/Sites-catalog-semparar/default/dwfd40f891/Regulamento_Sem-Parar-Pratico_03-2026.pdf";
  const idealRegulationUrl =
    "https://www.semparar.com.br/on/demandware.static/-/Sites-catalog-semparar/default/dwdeca2c5e/Sem%20Parar%20Ideal.pdf";

  const externalCheckoutBase =
    "http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow";
  const livreCheckoutUrl = `${externalCheckoutBase}?pid=004-LIVRE-MANUAL-FREEFLOW-PEDAGIOELETRONICO`;
  const praticoCheckoutUrl = `${externalCheckoutBase}?pid=002-ETL-PRATICO-94OFF-10M-PEDAGIOELETRONICO`;
  const idealCheckoutUrl = `${externalCheckoutBase}?pid=007-ETL-TURBINADO-IDEAL-91OFF-10M-PEDAGIOELETRONICO`;
  const flexCheckoutUrl = `${externalCheckoutBase}?pid=003-FLEX-25-PEDAGIOELETRONICO`;

  const standardWhereToUse: PlanItem[] = [
    { text: "Pedágios com cancela", included: true },
    { text: "Pedágios eletrônicos (Free Flow)", included: true },
    { text: "Estacionamentos", included: true },
    { text: "Abastecimentos", included: true },
    { text: "Drive-thrus", included: true },
    { text: "Lava-rápidos", included: true },
  ];

  const standardBenefits: PlanItem[] = [
    { text: "Cashback ao abastecer com a Tag", included: true },
    { text: "Um best-seller por mês no Skeelo", included: true },
    { text: "Desconto de 10% na Movida", included: true },
    { text: "Cashback no Sem Parar Shop", included: true },
  ];

  const plans: Plan[] = [
    {
      id: "livre",
      name: "Livre Free Flow",
      isBestSeller: true,
      badgeLabel: t("pages:success:newBadge"),
      promo: "Pedágios eletrônicos",
      originalPriceLabel: "Recarregue e use",
      price: "Sem mensalidade",
      tags: ["Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: livreCheckoutUrl,
      summaryHeader:
        "Plano gratuito para uso em pedágios eletrônicos (Free Flow).",
      summaryFeatures: [
        "Adesão grátis",
        "5% de desconto no Free Flow",
      ],
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          description: "Não possui coberturas inclusas",
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: [
            { text: "Pedágios com cancela*", included: true },
            { text: "Pedágios eletrônicos (Free Flow)", included: true },
            { text: "Estacionamentos*", included: true },
            { text: "Abastecimentos*", included: true },
            { text: "Lava-rápidos*", included: true },
            { text: "Drive-thrus*", included: true },
          ],
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: [
            { text: "Cashback ao abastecer com a Tag", included: false },
            { text: "Um best-seller por mês no Skeelo", included: false },
            { text: "Desconto de 10% na Movida", included: true },
            { text: "Cashback no Sem Parar Shop", included: true },
            {
              text: "Desconto de 5% a 70% na tarifa de pedágios sem cancelas (Free Flow)",
              included: true,
            },
            {
              text: "Evite multas! Receba automaticamente a tarifa na fatura Sem Parar",
              included: true,
            },
          ],
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          rechargeTable: [
            { recharge: "R$ 50,00", fee: "R$ 0,00", balance: "R$ 50,00" },
            { recharge: "R$ 80,00", fee: "R$ 0,00", balance: "R$ 80,00" },
            { recharge: "R$ 150,00", fee: "R$ 0,00", balance: "R$ 150,00" },
            { recharge: "R$ 200,00", fee: "R$ 0,00", balance: "R$ 200,00" },
            { recharge: "R$ 250,00", fee: "R$ 0,00", balance: "R$ 250,00" },
          ],
        },
        {
          id: "accession",
          title: t("pages:success:accession"),
          description: "Grátis",
        },
        {
          id: "maintenance",
          title: t("pages:success:maintenance"),
          description: "Sem cobrança",
        },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description:
            "Este plano é exclusivo para uso em pedágios eletrônicos.\n\n*Caso utilize em pedágios com cancela, estacionamentos, postos, drive-thrus ou lava-rápidos, haverá um acréscimo de R$29,90 na fatura.",
        },
      ],
    },
    {
      id: "pratico",
      name: "Conforto",
      promo: "94% OFF por 10 meses",
      originalPriceLabel: "De R$ 49,90 por",
      price: "R$ 2,89",
      pricePeriod: "/até o 10º mês",
      tags: ["Economize R$ 370", "Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: praticoCheckoutUrl,
      summaryFeatures: [
        "Pedágio e Free Flow",
        "Estacionamentos",
        "Abastecimentos",
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          items: [
            { text: "Proteções até 40 km", included: true },
            { text: "Socorro mecânico", included: true },
            { text: "Guincho após pane/pane seca", included: true },
            { text: "Guincho após acidente", included: true },
            { text: "Chaveiro automotivo", included: true },
            { text: "Troca de pneus", included: true },
            {
              text: "Indenização por invalidez permanente por acidente",
              included: true,
            },
            { text: "Perda de renda por desemprego", included: true },
            { text: "Perda de renda por incapacidade", included: true },
            { text: "Quitação de dívida por morte", included: true },
          ],
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: standardWhereToUse,
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: standardBenefits,
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "O valor com desconto na mensalidade é válido durante a quantidade de meses vigentes na promoção.\n\nApós o término da oferta, o valor de R$49,90/mês volta a ser cobrado.",
        },
        {
          id: "accession",
          title: t("pages:success:accession"),
          description: "Grátis",
        },
        {
          id: "maintenance",
          title: t("pages:success:maintenance"),
          description: "Sem cobrança",
        },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description: "Sem cobrança",
        },
        {
          id: "terms",
          title: t("pages:success:regulationTerms"),
          link: {
            label: "Plano Sem Parar Conforto",
            href: praticoRegulationUrl,
          },
        },
      ],
    },
    {
      id: "ideal",
      name: "Ultra",
      badgeLabel: t("pages:success:bestSeller"),
      promo: "91% OFF por 10 meses",
      originalPriceLabel: "De R$ 59,90 por",
      price: "R$ 5,39",
      pricePeriod: "/até o 10º mês",
      tags: ["Seguros inclusos", "Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: idealCheckoutUrl,
      summaryHeader:
        "Use em pedágios e no Free Flow, ao abastecer, estacionar e mais:",
      summaryFeatures: [
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
        "Seguro Acidentes Pessoais",
        "Rastreio veicular e outros",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          items: [
            { text: "Proteções até 40 km", included: true },
            { text: "Socorro mecânico", included: true },
            { text: "Guincho após pane/pane seca", included: true },
            { text: "Guincho após acidente", included: true },
            { text: "Chaveiro automotivo", included: true },
            { text: "Troca de pneus", included: true },
            { text: "Indenização por morte acidental", included: true },
            {
              text: "Indenização por invalidez permanente por acidente",
              included: true,
            },
            { text: "Assistência SOS Odonto", included: true },
            { text: "Alerta de débitos", included: true },
            { text: "Perda de renda por desemprego", included: true },
            { text: "Perda de renda por incapacidade", included: true },
            { text: "Quitação de dívida por morte", included: true },
            { text: "Rastreio do veículo por usos", included: true },
            { text: "Acesso aos trajetos do veículo", included: true },
            { text: "Cobertura da rede credenciada", included: true },
            { text: "Serviços de rastreio no SuperApp", included: true },
          ],
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: standardWhereToUse,
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: standardBenefits,
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "O valor com desconto na mensalidade é válido durante a quantidade de meses vigentes na promoção.\n\nApós o término da oferta, o valor de R$59,90/mês volta a ser cobrado.",
        },
        {
          id: "accession",
          title: t("pages:success:accession"),
          description: "Grátis",
        },
        {
          id: "maintenance",
          title: t("pages:success:maintenance"),
          description: "Sem cobrança",
        },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description: "Sem cobrança",
        },
        {
          id: "terms",
          title: t("pages:success:regulationTerms"),
          link: {
            label: "Plano Sem Parar Ultra",
            href: idealRegulationUrl,
          },
        },
      ],
    },
    {
      id: "flex",
      name: "Flex",
      promo: "Não usa, não paga!",
      originalPriceLabel: "De R$ 29,90 por",
      price: "R$ 25,90",
      pricePeriod: "/mês que usar",
      tags: ["Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: flexCheckoutUrl,
      summaryFeatures: ["Pedágio e Free Flow", "Estacionamentos (2 dias/mês)"],
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          description: "Não possui coberturas inclusas",
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: [
            { text: "Pedágios com cancela", included: true },
            { text: "Pedágios eletrônicos (Free Flow)", included: true },
            { text: "Estacionamentos (2 dias por mês)", included: true },
            { text: "Abastecimentos", included: false },
            { text: "Drive-thrus", included: false },
            { text: "Lava-rápidos", included: false },
          ],
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: [
            { text: "Cashback ao abastecer com a Tag", included: false },
            { text: "Um best-seller por mês no Skeelo", included: true },
            { text: "Desconto de 10% na Movida", included: true },
            { text: "Cashback no Sem Parar Shop", included: true },
          ],
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "A mensalidade é cobrada somente nos meses que utilizar a tag ao passar no pedágio ou estacionamento.",
        },
        {
          id: "accession",
          title: t("pages:success:accession"),
          description: "R$ 20,00",
        },
        {
          id: "maintenance",
          title: t("pages:success:maintenance"),
          description:
            "Após 90 dias sem uso, será cobrada manutenção de R$11,90/mês até que a Tag seja utilizada novamente.",
        },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description:
            "Você terá até 2 dias por mês para usar no estacionamento. A partir do 3º dia de uso, haverá um acréscimo de R$14,00 na mensalidade.",
        },
      ],
    },
  ];

  return ["livre", "ideal", "pratico", "flex"].map(
    (id) => plans.find((plan) => plan.id === id) as Plan,
  );
}

type PlanFace = {
  badgeLabel?: string;
  promo?: string;
  originalPriceLabel?: string;
  price: string;
  pricePeriod?: string;
  tags?: string[];
  summaryHeader?: string;
  summaryFeatures?: string[];
  summaryDisclaimer?: string;
  ctaLabel: string;
  highlighted: boolean;
  headerTone: "white" | "gray" | "pink";
  emphasizedCta: boolean;
  showDetails: boolean;
};

function getPlanFace(
  plan: Plan,
  isStack: boolean,
  t: (key: string) => string,
): PlanFace {
  if (!isStack) {
    return {
      badgeLabel: plan.badgeLabel,
      promo: plan.promo,
      originalPriceLabel: plan.originalPriceLabel,
      price: plan.price,
      pricePeriod: plan.pricePeriod,
      tags: plan.tags,
      summaryHeader: plan.summaryHeader,
      summaryFeatures: plan.summaryFeatures,
      summaryDisclaimer: plan.summaryDisclaimer,
      ctaLabel: plan.ctaLabel,
      highlighted: !!plan.isBestSeller,
      headerTone: "white",
      emphasizedCta: !!plan.isBestSeller,
      showDetails: true,
    };
  }

  if (plan.id === "livre") {
    return {
      badgeLabel: t("pages:success:enjoyBadge"),
      promo: plan.promo,
      originalPriceLabel: plan.originalPriceLabel,
      price: plan.price,
      tags: ["frete grátis"],
      summaryHeader:
        "Plano gratuito para uso em pedágios eletrônicos (Free Flow).",
      summaryFeatures: [
        "Adesão grátis",
        "5% de desconto no Free Flow",
      ],
      ctaLabel: t("pages:success:orderTag"),
      highlighted: true,
      headerTone: "pink",
      emphasizedCta: true,
      showDetails: true,
    };
  }

  if (plan.id === "pratico") {
    return {
      badgeLabel: t("pages:success:off92"),
      promo: "94% OFF por 10 meses",
      originalPriceLabel: "De R$49,90 por",
      price: "R$ 2,89",
      pricePeriod: "/até o 10º mês",
      tags: ["economize R$370", "frete grátis"],
      summaryFeatures: [
        "Pedágio e Free Flow",
        "Estacionamentos",
        "Abastecimentos",
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      ctaLabel: t("pages:success:orderTag"),
      highlighted: false,
      headerTone: "white",
      emphasizedCta: false,
      showDetails: true,
    };
  }

  if (plan.id === "ideal") {
    return {
      badgeLabel: t("pages:success:bestSeller"),
      promo: "91% OFF por 10 meses",
      originalPriceLabel: "De R$59,90 por",
      price: plan.price,
      pricePeriod: "/até o 10º mês",
      tags: ["seguros inclusos", "frete grátis"],
      summaryHeader:
        "Use em pedágios e no Free Flow, ao abastecer, estacionar e mais:",
      summaryFeatures: [
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
        "Seguro Acidentes Pessoais",
        "Rastreio veicular e outros",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      ctaLabel: t("pages:success:orderTag"),
      highlighted: false,
      headerTone: "white",
      emphasizedCta: false,
      showDetails: true,
    };
  }

  return {
    promo: "Não usa, não paga",
    originalPriceLabel: "Por apenas",
    price: "R$ 25,90",
    pricePeriod: "/mês que usar",
    tags: ["frete grátis"],
    summaryFeatures: [
      "Pedágio e Free Flow",
      "Estacionamentos (2 dias/mês)",
    ],
    summaryDisclaimer: "Sem fidelidade.",
    ctaLabel: t("pages:success:orderTag"),
    highlighted: false,
    headerTone: "white",
    emphasizedCta: false,
    showDetails: true,
  };
}

const PlanSectionView = ({
  section,
  alternate,
  fillRemaining = false,
}: {
  section: PlanSection;
  alternate: boolean;
  fillRemaining?: boolean;
}) => {
  const bg = alternate ? "bg-[#f9f9f9]" : "bg-white";
  const grow = fillRemaining ? "flex-1 xl:flex-none" : "";
  const baseClass = `${bg} ${grow} border-t border-[#e6e6e6] px-6 py-4 flex flex-col gap-2`;

  if (section.items) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        {section.items.map((item, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <FeatureIcon included={item.included} />
            <p className="flex-1 text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
              {item.text}
            </p>
          </div>
        ))}
        {section.notes?.map((note, idx) => (
          <p
            key={`note-${idx}`}
            className="text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px] pl-6"
          >
            {note}
          </p>
        ))}
        {section.link && (
          <a
            href={section.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-normal text-[#355da5] underline leading-[1.5] tracking-[0.021px] mt-1"
          >
            {section.link.label}
          </a>
        )}
      </div>
    );
  }

  if (section.rechargeTable) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        <RechargeTable rows={section.rechargeTable} />
      </div>
    );
  }

  if (!section.description && section.link) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        <a
          href={section.link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm font-normal text-[#355da5] leading-[1.5] tracking-[0.021px]"
        >
          <FileIcon />
          <span>{section.link.label}</span>
        </a>
      </div>
    );
  }

  return (
    <div className={baseClass}>
      <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
        {section.title}
      </p>
      {section.description?.split("\n\n").map((paragraph, idx) => (
        <p
          key={idx}
          className="text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px]"
        >
          {paragraph}
        </p>
      ))}
    </div>
  );
};

const RechargeTable = ({ rows }: { rows: PlanRechargeRow[] }) => {
  const { t } = useI18n();

  return (
    <div className="flex flex-col text-[12px] text-[#0e0d0d]">
      <div className="grid grid-cols-3 gap-2 pb-1 font-medium">
        <span>{t("pages:success:recharge")}</span>
        <span>{t("pages:success:fee")}</span>
        <span>{t("pages:success:balance")}</span>
      </div>
      {rows.map((row, idx) => (
        <div
          key={idx}
          className={`grid grid-cols-3 gap-2 px-1 py-1 rounded ${
            idx % 2 === 0 ? "bg-[#e6e6e6]" : ""
          }`}
        >
          <span className="font-light">{row.recharge}</span>
          <span className="font-light">{row.fee}</span>
          <span>{row.balance}</span>
        </div>
      ))}
    </div>
  );
};

const PlanCard = ({
  plan,
  isOpen,
  onToggle,
  layout = "carousel",
}: {
  plan: Plan;
  isOpen: boolean;
  onToggle: () => void;
  layout?: "carousel" | "stack";
}) => {
  const { t } = useI18n();
  const isStack = layout === "stack";
  const face = getPlanFace(plan, isStack, t);

  const badgeClassName = isStack
    ? `${face.highlighted ? "bg-primary" : "bg-text"} text-white text-sm font-bold font-primary leading-[1.4] tracking-[-0.1px] px-1 rounded-[10px] whitespace-nowrap`
    : face.emphasizedCta
      ? "bg-[#d60b52] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md whitespace-nowrap"
      : "bg-[#363636] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md whitespace-nowrap";

  const ctaClassName = face.emphasizedCta
    ? isStack
      ? "w-full h-[50px] bg-primary text-white font-bold font-primary text-base uppercase flex items-center justify-center rounded-theme-large"
      : "w-full h-12 bg-[#d60b52] hover:opacity-90 text-white font-bold text-sm flex items-center justify-center rounded-lg transition-opacity uppercase"
    : isStack
      ? "w-full h-[50px] bg-foreground text-text border border-text font-bold font-primary text-base uppercase flex items-center justify-center rounded-theme-large"
      : "w-full h-12 bg-white hover:bg-gray-50 text-[#0e0d0d] border border-[#0e0d0d] font-bold text-sm flex items-center justify-center rounded-lg transition-colors uppercase";

  const hasSummary =
    !!face.summaryHeader ||
    !!face.summaryDisclaimer ||
    (face.summaryFeatures && face.summaryFeatures.length > 0);

  const hasTermsSection = plan.sections.some((s) => s.id === "terms");
  const rowSpanClass = hasTermsSection ? "xl:row-span-13" : "xl:row-span-12";
  const headerToneClass =
    face.headerTone === "gray"
      ? "bg-[#f3f3f3]"
      : face.headerTone === "pink"
        ? "bg-[#fff0f4]"
        : "bg-white";
  const isHighlightedStack = isStack && face.highlighted;
  const stackEdgeClass = isHighlightedStack
    ? "border-primary"
    : "border-[#e6e6e6]";

  const header = (
    <div
      className={
        isStack
          ? `${headerToneClass} px-6 py-4 flex items-center gap-2.5 border-t border-x ${stackEdgeClass} rounded-t-2xl${
              isHighlightedStack ? "" : " border-b border-[#e6e6e6]"
            }`
          : "bg-white px-6 h-[50px] flex items-center gap-2"
      }
    >
      <p
        className={`flex-1 text-[18px] text-text font-primary ${
          isStack
            ? "font-bold leading-[1.2] tracking-[-0.1px]"
            : `leading-[1.3] ${face.emphasizedCta ? "font-extrabold" : "font-semibold"}`
        }`}
      >
        {plan.name}
      </p>
      {face.badgeLabel && (
        <span className={badgeClassName}>{face.badgeLabel}</span>
      )}
    </div>
  );

  const offerBlock = (
    <div
      data-testid={isHighlightedStack ? `plan-${plan.id}-body` : undefined}
      className={
        isStack
          ? `bg-foreground px-6 py-4 flex flex-col gap-6 border-x border-b ${stackEdgeClass}`
          : "border-t border-[#e6e6e6] bg-white px-5 py-4 flex flex-col gap-3"
      }
    >
      <div className={isStack ? "flex flex-col gap-3" : "contents"}>
        {face.promo && (
          <p
            className={
              isStack
                ? "text-base font-bold font-primary text-text leading-[1.4] tracking-[-0.1px]"
                : "text-[16px] font-semibold text-[#0e0d0d] leading-[1.3]"
            }
          >
            {face.promo}
          </p>
        )}
        <div className="flex flex-col">
          {face.originalPriceLabel && (
            <p className="text-xs font-normal font-primary text-[#696966] leading-[1.3]">
              {face.originalPriceLabel}
            </p>
          )}
          <p className="text-text flex items-end gap-2.5">
            <span
              className={
                isStack
                  ? "text-base font-bold font-primary leading-[1.4] tracking-[-0.1px]"
                  : "text-[24px] font-extrabold leading-[1.2] tracking-[-0.2px]"
              }
            >
              {face.price}
            </span>
            {face.pricePeriod && (
              <span className="text-xs font-normal font-primary leading-[1.3]">
                {face.pricePeriod}
              </span>
            )}
          </p>
        </div>
        <div className={`flex flex-wrap gap-2 ${isStack ? "" : "min-h-[28px]"}`}>
          {face.tags?.map((tag, index) => (
            <span
              key={index}
              className={
                isStack
                  ? "bg-text text-white text-sm font-bold font-primary leading-[1.4] tracking-[-0.1px] px-1 rounded-[10px]"
                  : "bg-[#363636] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md"
              }
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {hasSummary && isStack && (
        <div className="flex flex-col gap-2">
          {face.summaryHeader && (
            <p className="text-sm font-normal font-primary text-text leading-[1.4] tracking-[-0.1px]">
              {face.summaryHeader}
            </p>
          )}
          {face.summaryFeatures?.map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <FeatureIcon included />
              <span className="text-sm font-normal font-primary text-text leading-[1.4] tracking-[-0.1px]">
                {feature}
              </span>
            </div>
          ))}
          {face.summaryDisclaimer && (
            <p className="text-xs font-normal font-primary text-text leading-[1.3] mt-0">
              {face.summaryDisclaimer}
            </p>
          )}
        </div>
      )}

      {isStack && (
        <a
          href={plan.ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className={ctaClassName}
        >
          {face.ctaLabel}
        </a>
      )}
    </div>
  );

  const detailsToggle = face.showDetails ? (
    <button
      type="button"
      onClick={onToggle}
      className={
        isStack
            ? `flex items-center justify-between gap-2 px-6 py-3 bg-[#f3f3f3] text-[#696966] text-sm font-primary leading-[1.4] tracking-[-0.1px] border border-[#e6e6e6] ${
                isOpen ? "" : "rounded-b-2xl"
              }`
          : "flex items-center justify-between gap-2 px-5 py-3 border-t border-[#e6e6e6] bg-white text-[#696966] text-sm leading-[1.5] tracking-[0.021px] hover:text-[#0e0d0d] transition-colors"
      }
      aria-expanded={isOpen}
      aria-controls={`plan-${plan.id}-details`}
    >
      <span>
        {isOpen
          ? t("pages:success:hidePlanDetails")
          : t("pages:success:viewPlanDetails")}
      </span>
      <ChevronIcon open={isOpen} />
    </button>
  ) : null;

  const expandedSections =
    face.showDetails && isOpen
      ? plan.sections.map((section, index) => (
          <PlanSectionView
            key={section.id}
            section={section}
            alternate={index % 2 === 1}
          />
        ))
      : null;

  return (
    <div
      className={`relative self-stretch flex flex-col ${
        isStack
          ? "w-full min-w-0 max-w-none"
          : `overflow-hidden bg-white rounded-[16px] border border-[#e6e6e6] min-w-[280px] max-w-[280px] xl:max-w-none flex-shrink-0 snap-center xl:flex-shrink ${rowSpanClass} xl:grid xl:grid-rows-subgrid`
      }`}
    >
      {face.highlighted && !isStack && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[16px] z-20"
          style={{
            padding: "1.5px",
            background:
              "linear-gradient(to bottom, #d60b52 0%, rgba(214, 11, 82, 0.7) 25%, rgba(214, 11, 82, 0.35) 55%, rgba(214, 11, 82, 0.1) 85%, transparent 100%)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
          }}
        />
      )}

      {isStack ? (
        <>
          {header}
          {offerBlock}
          {detailsToggle}
          {expandedSections}
        </>
      ) : (
        <>
          {header}
          {offerBlock}
          {hasSummary && (
            <div className="bg-white px-5 py-4 flex flex-col gap-3">
              {face.summaryHeader && (
                <p className="text-sm text-[#0e0d0d] leading-[1.4]">
                  {face.summaryHeader}
                </p>
              )}
              {face.summaryFeatures && face.summaryFeatures.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {face.summaryFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FeatureIcon included />
                      <span className="text-sm text-[#0e0d0d] leading-[1.4]">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {face.summaryDisclaimer && (
                <p className="text-xs text-[#696966] leading-[1.4] mt-2">
                  {face.summaryDisclaimer}
                </p>
              )}
            </div>
          )}
          <div className="bg-white px-5 py-4 mt-auto">
            <a
              href={plan.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className={ctaClassName}
            >
              {face.ctaLabel}
            </a>
          </div>
          {detailsToggle}
          {expandedSections}
        </>
      )}
    </div>
  );
};

export default function PlanSelection({
  layout = "carousel",
}: {
  layout?: "carousel" | "stack";
}) {
  const { t } = useI18n();
  const plans = buildPlans(t);
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const [areDetailsOpen, setAreDetailsOpen] = useState(false);
  const isStack = layout === "stack";

  const toggleDetails = (planId: string) => {
    if (isStack) {
      setOpenPlanId((current) => (current === planId ? null : planId));
      return;
    }

    setAreDetailsOpen((open) => !open);
  };

  const isPlanOpen = (planId: string) =>
    isStack ? openPlanId === planId : areDetailsOpen;

  return (
    <div className={isStack ? "w-full flex-1 flex flex-col min-h-full" : "w-full"}>
      <div className={isStack ? "w-full max-w-[480px] mx-auto px-4" : undefined}>
      {isStack && (
        <div className="flex flex-col gap-2 pt-6 pb-2 px-1">
          <h2 className="text-2xl font-bold font-primary text-text leading-normal">
            {t("pages:success:chooseYourPlan")}
          </h2>
          <p className="text-sm font-normal font-primary text-[#696966] leading-normal">
            {t("pages:success:chooseYourPlanDescription")}
          </p>
        </div>
      )}
      {!isStack && (
        <div className="text-left md:text-center mb-6 px-2 md:px-0">
          <p
            className="text-base text-[#0e0d0d] font-medium leading-[1.3] tracking-[-0.4px]"
            style={{ fontFamily: "Ubuntu, sans-serif" }}
          >
            {t("pages:success:choosePlan")}
            <span className="text-[#d60b52]">{` tag Sem Parar`}</span>
            {t("pages:success:choosePlanSuffix")}
          </p>
        </div>
      )}

      <div
        data-testid="plan-selection-list"
        className={
          isStack
            ? "flex flex-col gap-5 pt-4 pb-8"
            : "flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory items-stretch xl:overflow-x-visible xl:-mx-0 xl:px-0 xl:snap-none xl:grid xl:justify-center xl:grid-cols-[repeat(4,minmax(280px,320px))] xl:gap-x-4 xl:gap-y-0"
        }
        style={
          isStack
            ? undefined
            : {
                gridTemplateRows:
                  "repeat(13, auto)" /* header + miolo + summary + CTA + toggle + 7 sections + terms (Conforto/Ultra) */,
              }
        }
      >
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isOpen={isPlanOpen(plan.id)}
            onToggle={() => toggleDetails(plan.id)}
            layout={layout}
          />
        ))}
      </div>
      </div>

      {isStack && (
        <div
          data-testid="plans-catalog-footer"
          className="w-full flex-1 bg-[#f3f3f3] border-t border-[#e6e6e6] flex flex-col items-center gap-3 pt-4 pb-10 px-6"
        >
          <p className="w-full text-xs font-normal font-primary text-[#696966] leading-[1.4] text-center">
            {t("pages:success:plansCatalogNotes")}
          </p>
          <a
            href="/faq"
            className="text-xs font-bold font-primary text-[#363636] underline"
          >
            {t("pages:success:frequentQuestions")}
          </a>
        </div>
      )}
    </div>
  );
}
